import {
  CobraA,
  IdCompany,
  PaymentCompany,
  PaymentCompanyDto,
  PaymentCompanyListResponse,
} from '@app/shared/models/polipay-payment.model';

/**
 * Normaliza un objeto crudo de `/companies/` a `PaymentCompany`.
 *
 * Acepta snake_case y camelCase: según la versión del serializer Django el
 * endpoint puede devolver `id_company` o `idCompany`, etc.
 *
 * Los tres campos nuevos del contrato §Companies rev. 2026-09-25 (`legal_name`,
 * `org_company_id`, `external_ref`) son opcionales: si el DTO no los trae, se
 * dejan `undefined` — la pantalla decide qué pinta con eso.
 */
export function mapCompany(dto: PaymentCompanyDto): PaymentCompany {
  return {
    // El API real devuelve `id`; los aliases cubren variantes de serializer.
    // ⚠️ Es el número INTERNO de Payments: identifica la fila en pantalla y NUNCA
    // viaja como `id_company` (el back responde 403/404). Lo que viaja sale de
    // `resolvePaymentsCompanyId` (org_company_id → external_ref → ninguno).
    id_company: (dto.id ?? dto.id_company ?? dto.idCompany) as IdCompany,
    // El API real devuelve `name`; los aliases cubren variantes de serializer
    company_name: dto.name ?? dto.company_name ?? dto.companyName ?? '',
    // Contrato §Companies rev. 2026-09-25 — snake/camel fallback
    legal_name: dto.legal_name ?? dto.legalName,
    org_company_id: (dto.org_company_id ?? dto.orgCompanyId) as IdCompany | null | undefined,
    external_ref: dto.external_ref ?? dto.externalRef,
    cobra_a: (dto.cobra_a ?? dto.cobraA ?? 'SIN CONFIGURAR') as CobraA,
    gateway_account_id: dto.gateway_account_id ?? dto.gatewayAccountId ?? null,
    is_active: dto.is_active ?? dto.isActive,
  };
}

/**
 * Desenvuelve la respuesta de `GET /companies/`. Tolera tanto un sobre
 * `{count, results}` como un array pelado (resilencia ante cambios de contrato).
 */
export function toCompanyListResponse(raw: unknown): PaymentCompanyListResponse {
  if (Array.isArray(raw)) {
    const results = raw.map((r) => mapCompany(r as PaymentCompanyDto));
    return { count: results.length, results };
  }
  const envelope = (raw ?? {}) as { count?: number; results?: PaymentCompanyDto[] };
  const results = (envelope.results ?? []).map(mapCompany);
  return { count: envelope.count ?? results.length, results };
}
